import { Transform, Type } from 'class-transformer';
import {
  ArrayMaxSize,
  ArrayMinSize,
  IsArray,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsObject,
  IsOptional,
  Length,
  Max,
  MaxLength,
  Min,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { sanitizeString } from '../utils';
import { CaseLineDto } from './case-line.dto';
import { CasePickupValidateDto } from './case-pickup-validate.dto';
import { CaseReturnRequestType } from './case-return-request.enum';
import { CaseType } from './case-type.enum';

const MAX_LINE_ITEMS = 500;

/**
 * DTO that represents a valid Case
 */
export class CaseValidateDto {
  /**
   * Account ID against which the should be created
   */
  @IsNotEmpty()
  @Length(18, 24)
  public accountId: string;

  /**
   * Type of the Case
   */
  @IsIn([CaseType.Return, CaseType.Warranty])
  public type: CaseType;

  /**
   * Selected products/lines for the Case
   */
  @IsArray()
  @ArrayMinSize(1)
  @ArrayMaxSize(MAX_LINE_ITEMS)
  public lines: CaseLineDto[];

  /**
   * Return request type. Can be Pickup or Label
   */
  @IsOptional()
  @IsIn([CaseReturnRequestType.Label, CaseReturnRequestType.Pickup])
  requestType?: CaseReturnRequestType;

  /**
   * Reason for creating the Case
   */
  @IsNotEmpty()
  @Length(2, 10)
  public reason: string;

  /**
   * Pickup information for Return Case
   */
  @ValidateIf((body) => !!body?.pickup)
  @ValidateNested()
  @IsObject()
  @Type(() => CasePickupValidateDto)
  public pickup?: CasePickupValidateDto;

  /**
   * Description of the reason or general information about the Case
   */
  @IsOptional()
  @MaxLength(1000)
  @Transform(({ value }) => sanitizeString(value))
  public description?: string;

  /**
   * Number of pellets that conatins the return items.
   * For return pickup requests - Either the palletCount or the parcelsCount must be specified
   * For return label requests - parcelsCount must be specified
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  parcelCount?: number;

  /**
   * Number of parcel that conatins the return items.
   * For return pickup requests - either the palletCount or the parcelsCount must be specified
   * For return label requests - palletCount is not required
   */
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(100)
  palletCount?: number;
}
